import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Zap, Star, Crown } from 'lucide-react';

const rarityConfig = {
  common: {
    color: '#A0A0A0',
    glowColor: 'rgba(160, 160, 160, 0.6)',
    bgGradient: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
    particles: 20,
    screenShake: false,
    soundText: 'NICE!',
  },
  rare: {
    color: '#00CCFF',
    glowColor: 'rgba(0, 204, 255, 0.8)',
    bgGradient: 'linear-gradient(135deg, #001a33 0%, #003366 100%)',
    particles: 40,
    screenShake: false,
    soundText: 'SWEET!',
  },
  epic: {
    color: '#FF00FF',
    glowColor: 'rgba(255, 0, 255, 0.8)',
    bgGradient: 'linear-gradient(135deg, #1a001a 0%, #330033 100%)',
    particles: 60,
    screenShake: true,
    soundText: 'EPIC!',
  },
  legendary: {
    color: '#FFD700',
    glowColor: 'rgba(255, 215, 0, 0.9)',
    bgGradient: 'linear-gradient(135deg, #1a1a00 0%, #333300 100%)',
    particles: 100,
    screenShake: true,
    soundText: 'LEGENDARY!!!',
  },
};

const Particle = ({ delay, config }) => {
  const randomX = Math.random() * 200 - 100;
  const randomY = Math.random() * 200 - 100;
  const size = Math.random() * 8 + 4;
  
  return (
    <motion.div
      className="absolute rounded-full"
      style={{
        width: size,
        height: size,
        background: config.color,
        boxShadow: `0 0 ${size * 2}px ${config.glowColor}`,
        left: '50%',
        top: '50%',
      }}
      initial={{ x: 0, y: 0, scale: 0, opacity: 0 }}
      animate={{
        x: [0, randomX * 2, randomX * 4],
        y: [0, randomY * 2, randomY * 4],
        scale: [0, 1.5, 0],
        opacity: [0, 1, 0],
      }}
      transition={{
        duration: 1.5,
        delay: delay,
        ease: 'easeOut',
      }}
    />
  );
};

const PackOpeningAnimation = ({ card, onComplete }) => {
  const [phase, setPhase] = useState('shake'); // shake, explosion, reveal
  const [showParticles, setShowParticles] = useState(false);
  
  const config = rarityConfig[card?.rarity] || rarityConfig.common;
  
  useEffect(() => {
    // Phase 1: Shake the pack (1.5s)
    const shakeTimer = setTimeout(() => {
      setPhase('explosion');
      setShowParticles(true);
    }, 1500);
    
    // Phase 2: Explosion (1s)
    const explosionTimer = setTimeout(() => {
      setPhase('reveal');
    }, 2500);
    
    // Complete animation
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 5000);
    
    return () => {
      clearTimeout(shakeTimer);
      clearTimeout(explosionTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);
  
  return (
    <motion.div
      className="fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden"
      style={{ background: config.bgGradient }}
      initial={{ opacity: 0 }}
      animate={{ 
        opacity: 1,
        x: config.screenShake && phase === 'explosion' ? [0, -10, 10, -5, 5, 0] : 0,
      }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Background glow */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at center, ${config.glowColor} 0%, transparent 70%)`,
        }}
        animate={{
          scale: phase === 'explosion' ? [1, 3, 1.5] : 1,
          opacity: phase === 'explosion' ? [0.5, 1, 0.3] : 0.3,
        }}
        transition={{ duration: 1 }}
      />
      
      {/* Particles */}
      {showParticles && (
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(config.particles)].map((_, i) => (
            <Particle key={i} delay={i * 0.02} config={config} />
          ))}
        </div>
      )}
      
      <AnimatePresence mode="wait">
        {/* Phase 1: Shaking Pack */}
        {phase === 'shake' && (
          <motion.div
            key="shake"
            className="relative"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              rotate: [0, -5, 5, -3, 3, 0],
              x: [0, -10, 10, -5, 5, 0],
            }}
            exit={{ scale: 1.5, opacity: 0 }}
            transition={{
              rotate: { duration: 0.5, repeat: Infinity },
              x: { duration: 0.3, repeat: Infinity },
            }}
          >
            {/* Mystery Box Visual */}
            <div 
              className="w-64 h-80 rounded-2xl border-4 flex items-center justify-center relative overflow-hidden"
              style={{
                borderColor: config.color,
                background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%)',
                boxShadow: `0 0 40px ${config.glowColor}`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 0.5, repeat: Infinity }}
              >
                <Sparkles className="w-24 h-24" style={{ color: config.color }} />
              </motion.div>
              <p 
                className="absolute bottom-4 font-anton text-xl uppercase tracking-widest"
                style={{ color: config.color }}
              >
                OPENING...
              </p>
            </div>
          </motion.div>
        )}
        
        {/* Phase 2: Explosion */}
        {phase === 'explosion' && (
          <motion.div
            key="explosion"
            className="relative"
            initial={{ scale: 0 }}
            animate={{ scale: [0, 3, 0] }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Light burst */}
            <div 
              className="w-64 h-64 rounded-full"
              style={{
                background: `radial-gradient(circle, ${config.color} 0%, transparent 70%)`,
              }}
            />
            
            {/* Sound cue text */}
            <motion.p
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-anton text-6xl whitespace-nowrap"
              style={{ color: config.color, textShadow: `0 0 30px ${config.glowColor}` }}
              initial={{ scale: 0, rotate: -30 }}
              animate={{ scale: [0, 1.5, 1], rotate: [0, 10, 0] }}
              transition={{ duration: 0.5 }}
            >
              {config.soundText}
            </motion.p>
          </motion.div>
        )}
        
        {/* Phase 3: Card Reveal */}
        {phase === 'reveal' && card && (
          <motion.div
            key="reveal"
            className="relative flex flex-col items-center"
            initial={{ scale: 0, rotateY: 180 }}
            animate={{ scale: 1, rotateY: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          >
            {/* Card Image */}
            <motion.div
              className="relative w-72 h-96 rounded-2xl overflow-hidden"
              style={{
                boxShadow: `0 0 60px ${config.glowColor}, inset 0 0 30px ${config.glowColor}`,
                border: `4px solid ${config.color}`,
              }}
              animate={{
                boxShadow: [
                  `0 0 60px ${config.glowColor}`,
                  `0 0 100px ${config.glowColor}`,
                  `0 0 60px ${config.glowColor}`,
                ],
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <img
                src={`data:image/png;base64,${card.image_base64}`}
                alt={card.character_name}
                className="w-full h-full object-cover"
              />
              
              {/* Holographic overlay */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `linear-gradient(45deg, transparent 30%, ${config.color}20 50%, transparent 70%)`,
                }}
                animate={{ x: [-200, 200] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              />
            </motion.div>
            
            {/* Card Info */}
            <motion.div
              className="mt-6 text-center"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <h2 
                className="font-anton text-4xl uppercase"
                style={{ color: config.color, textShadow: `0 0 20px ${config.glowColor}` }}
              >
                {card.character_name}
              </h2>
              <p className="font-bebas text-xl text-white/80 mt-1">{card.title}</p>
              
              {/* Stars */}
              <div className="flex justify-center gap-1 mt-3">
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                  >
                    <Star
                      className={`w-8 h-8 ${
                        i < card.power_rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
                      }`}
                    />
                  </motion.div>
                ))}
              </div>
              
              {/* Rarity Badge */}
              <motion.div
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full"
                style={{ 
                  background: `${config.color}30`,
                  border: `2px solid ${config.color}`,
                }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.8, type: 'spring' }}
              >
                {card.rarity === 'legendary' && <Crown className="w-5 h-5" style={{ color: config.color }} />}
                {card.rarity === 'epic' && <Zap className="w-5 h-5" style={{ color: config.color }} />}
                <span 
                  className="font-anton uppercase text-lg"
                  style={{ color: config.color }}
                >
                  {card.rarity}
                </span>
              </motion.div>
            </motion.div>
            
            {/* Click to continue */}
            <motion.p
              className="absolute bottom-[-60px] font-space text-white/40 text-sm"
              animate={{ opacity: [0.4, 0.8, 0.4] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              Click anywhere to continue...
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PackOpeningAnimation;
